import { isVisible } from "../helpers.js"
import { setupMobileSheetGesture } from "./mobile_sheet_gesture.js"
import { lockPageScroll, unlockPageScroll } from "./page_scroll_lock.js"

const mobileViewport = window.matchMedia("(max-width: 639px)")
const dialogFocusOrigins = new WeakMap()

export const ModalDialog = {
  mounted() {
    const opener = document.activeElement
    // A replacement dialog may outlive the dialog containing its opener.
    // Keep that dialog's origins so dismissal can return to the page control.
    this.focusOrigins = [
      opener,
      ...(dialogFocusOrigins.get(opener?.closest("dialog")) || []),
    ]
    dialogFocusOrigins.set(this.el, this.focusOrigins)
    this.ensureOpen()
    lockPageScroll()
    this.scrim = this.el.querySelector("[data-modal-scrim]")
    this.onScrimClick = (event) => {
      if (
        mobileViewport.matches &&
        this.el.dataset.modalMobilePresentation === "sheet"
      ) {
        event.preventDefault()
        event.stopImmediatePropagation()
      }
    }
    this.scrim.addEventListener("click", this.onScrimClick, true)
    this.destroyMobileSheetGesture = setupMobileSheetGesture(
      this.el.querySelector("[data-modal-sheet]"),
    )

    this.onCancel = (event) => {
      event.preventDefault()

      // A legacy modal can be rendered inside this dialog on purpose --
      // showModal() puts us in the top layer and makes everything outside
      // inert, so a nested editor rendered as a sibling would be unreachable.
      // Its cancel button carries phx-hook="ModalCancel", but
      // ModalCancelManager bails on any keydown whose target is inside a
      // <dialog>, so Escape arrives here as the native `cancel` event instead.
      // Dismissing this dialog would throw away the parent form the user is
      // still filling in, so close the innermost nested editor first.
      const nested = Array.from(
        this.el.querySelectorAll('[phx-hook="ModalCancel"]'),
      ).filter(isVisible)

      if (nested.length > 0) {
        nested[nested.length - 1].click()
        return
      }

      this.el.querySelector("[data-modal-dismiss]")?.click()
    }

    this.el.addEventListener("cancel", this.onCancel)
  },

  updated() {
    // `open` is reflected as an HTML attribute. A LiveView patch can remove
    // that client-owned attribute while updating form content inside the
    // dialog, so restore the native top-layer state after every patch.
    this.ensureOpen()
  },

  ensureOpen() {
    if (this.el.open) return

    // showModal() re-runs the native dialog focusing steps, which would move
    // focus to the dismiss button. These modals host phx-change forms, so a
    // response mid-typing must not steal the caret; restore whatever inside
    // the dialog had focus. On mount nothing inside it does, so the native
    // focusing steps still apply.
    const previous = document.activeElement
    const restore = previous && this.el.contains(previous) ? previous : null

    this.el.showModal()

    if (restore && restore.isConnected) restore.focus({ preventScroll: true })
  },

  destroyed() {
    this.el.removeEventListener("cancel", this.onCancel)
    this.scrim.removeEventListener("click", this.onScrimClick, true)
    this.destroyMobileSheetGesture()
    if (this.el.open) this.el.close()
    unlockPageScroll()
    // LiveView has already detached the dialog, so close() cannot run the
    // browser's connected-dialog focus restoration steps. Only recover focus
    // lost to detachment; navigation or a newer dialog may already own it.
    const active = document.activeElement
    const opener = this.focusOrigins.find((element) => element?.isConnected)
    if ((!active || active === document.body) && opener) {
      opener.focus({ preventScroll: true })
    }
    dialogFocusOrigins.delete(this.el)
  },
}

export const ModalSheetGesture = {
  mounted() {
    this.destroyMobileSheetGesture = setupMobileSheetGesture(this.el)
  },

  destroyed() {
    this.destroyMobileSheetGesture()
  },
}
